import { Injectable } from '@angular/core';
import { CreateCareerPathwayDto } from '@app/domain/career_pathway/model/create-career-pathway.model';
import { CareerPathway } from '@app/domain/career_pathway/model/career-pathway.model';
import { CareerPathwayAPIService } from '@app/domain/career_pathway/api/career-pathway-api.service';
import { NotifyUtils } from '@app/shared/utils/notify.utils';
import { Observable, firstValueFrom } from 'rxjs';
import { CareerPathwayMessages } from '../locale/career-pathway.messages';
import { Page } from '@app/models/api/page.model';
import { PagingParams } from '@app/models/api/paging-params.model';
import { SortingParams } from '@app/models/api/sorting-params.model';
import { CareerPathwayNode } from '../model/career-pathway-node.model';
import { InFlightRegistry } from '@app/shared/utils/in-flight-registry';

@Injectable({
  providedIn: 'root'
})
export class CareerPathwaysBusinessService {

  // key -> the write currently in flight for that pathway / user selection. A duplicate call while
  // one is in flight is coalesced onto the same promise (see InFlightRegistry) rather than rejected.
  private updating: InFlightRegistry;
  private updatingUsers: InFlightRegistry;
  uploadingFile: boolean;

  constructor(
    private careerPathwayAPI: CareerPathwayAPIService,
    private notifyUtils: NotifyUtils
  ) {
    this.updating = new InFlightRegistry();
    this.updatingUsers = new InFlightRegistry();
    this.uploadingFile = false;
  }

  get(
    ids: number[],
    name: string,
    roleIds: number[],
    showPrivate: boolean
  ): Observable<CareerPathway[]> {
    return this.careerPathwayAPI.get(ids, name, roleIds, showPrivate);
  }

  getPaginated(
    pagingParams: PagingParams,
    sortingParams: SortingParams,
    ids: number[],
    name: string,
    roleIds: number[],
    showPrivate: boolean
  ): Observable<Page<CareerPathway>> {
    return this.careerPathwayAPI.getPaginated(pagingParams, sortingParams, ids, name, roleIds, showPrivate);
  }

  create(createDto: CreateCareerPathwayDto): Promise<CareerPathway> {
    return firstValueFrom(this.careerPathwayAPI.create(createDto))
      .then(res => {
        this.notifyUtils.notify(CareerPathwayMessages.SUCCESS_CREATE);
        return res;
      });
  }

  update(id: number, updateDto: CreateCareerPathwayDto): Promise<CareerPathway> {
    return this.updating.run(id, () =>
      firstValueFrom(this.careerPathwayAPI.update(id, updateDto))
        .then(res => {
          this.notifyUtils.notify(CareerPathwayMessages.SUCCESS_UPDATE);
          return res;
        })
    );
  }

  makePublic(id: number): Promise<CareerPathway> {
    return this.updating.run(id, () =>
      firstValueFrom(this.careerPathwayAPI.makePublic(id))
        .then(res => {
          this.notifyUtils.notify(CareerPathwayMessages.SUCCESS_MAKE_PUBLIC);
          return res;
        })
    );
  }

  delete(id: number): Promise<CareerPathway> {
    return this.updating.run(id, () =>
      firstValueFrom(this.careerPathwayAPI.delete(id))
        .then(res => {
          this.notifyUtils.notify(CareerPathwayMessages.SUCCESS_DELETE);
          return res;
        })
    );
  }

  getSelectedCareerPathwayForUserId(userId: number): Observable<CareerPathway> {
    return this.careerPathwayAPI.getSelectedCareerPathwayForUserId(userId);
  }

  setSelectedCareerPathwayForUserId(userId: number, careerPathwayId: number): Promise<CareerPathway> {
    return this.updatingUsers.run(careerPathwayId, () =>
      firstValueFrom(this.careerPathwayAPI.setSelectedCareerPathwayForUserId(userId, careerPathwayId))
        .then(res => {
          this.notifyUtils.notify(CareerPathwayMessages.SUCCESS_UPDATE_MY_PATHWAY);
          return res;
        })
    );
  }

  getCareerPathwayNodeByCurrentRoleId(roleId: number): Observable<CareerPathwayNode> {
    return this.careerPathwayAPI.getCareerPathwayNodeByCurrentRoleId(roleId);
  }

  getMostRelevantPathwayForMe(): Observable<CareerPathway> {
    return this.careerPathwayAPI.getMostRelevantPathwayForMe();
  }
}
